/**
 * VRG-455 — the brand's own record of sales it has assigned to a buyer without
 * naming a certificate.
 *
 *   GET  /pending-ownership/admin               list
 *   POST /pending-ownership/admin               record one
 *   POST /pending-ownership/admin/:id/void      retract one
 *
 * Every call is company-scoped SERVER-SIDE (the company comes from the request
 * context, not from a parameter here), so nothing in this file can widen it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

// ─── Types ─────────────────────────────────────────────────────────────────────

export type PendingSaleStatus = 'pending' | 'resolved' | 'voided'

export type PendingSaleProduct = {
  id: string
  title: string
  sku: string | null
  image: string | null
}

export type PendingSale = {
  id: string
  company_id: string
  product_id: string
  /**
   * The identity anchor. Matched at claim time against every VERIFIED address on
   * the scanning buyer's account, not only against this exact string — so a
   * buyer who ordered with a work address and signed up with a personal one can
   * still resolve.
   */
  buyer_email: string
  /** The second anchor. Captured when the channel supplies it; never required. */
  buyer_phone: string | null
  status: PendingSaleStatus
  source_kind: string | null
  source_reference: string | null
  /** What the buyer's scan actually bound. Null until they scan. */
  resolved_certificate_id: string | null
  resolved_at: string | null
  voided_at: string | null
  reminder_count: number
  reminder_last_sent_at: string | null
  created_at: string
  updated_at: string | null
  product: PendingSaleProduct | null
}

export type PendingSalesResponse = {
  items: PendingSale[]
  total: number
  limit: number
  offset: number
}

export type PendingSalesParams = {
  /** One or more statuses. Omitted means every status. */
  statuses?: PendingSaleStatus[]
  /** Case-insensitive substring of the buyer email. */
  search?: string
  limit?: number
  offset?: number
}

export const pendingSalesKeys = {
  all: ['pending-sales'] as const,
  list: (params: PendingSalesParams) => ['pending-sales', 'list', params] as const,
}

/**
 * `createApiCall`'s errorHandler RESOLVES with `error.response` on 4xx/5xx
 * rather than rejecting, so every mutation here has to test the status itself.
 * Without it a 403 reads as success and the caller's onError is dead code —
 * the same trap `useUpdatePackage` and `useAssignPackageProduct` document.
 */
const assertOk = (response: any, fallback: string) => {
  const status = response?.status
  if (typeof status === 'number' && (status < 200 || status >= 300)) {
    const error: Error & { status?: number } = new Error(
      (response?.data as any)?.message || fallback,
    )
    error.status = status
    throw error
  }
}

// ─── Read ──────────────────────────────────────────────────────────────────────

export const usePendingSales = (params: PendingSalesParams = {}) =>
  useQuery({
    queryKey: pendingSalesKeys.list(params),
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/pending-ownership/admin',
        params: {
          status: params.statuses?.length ? params.statuses.join(',') : undefined,
          search: params.search || undefined,
          limit: params.limit,
          offset: params.offset,
        },
      })
      assertOk(response, 'Could not load recorded sales')
      return response.data as PendingSalesResponse
    },
  })

// ─── Write ─────────────────────────────────────────────────────────────────────

export type RecordSaleParams = {
  productId: string
  buyerEmail: string
  /** Sent only when present — the column is nullable and an empty string is not a phone. */
  buyerPhone?: string
}

export const useRecordSale = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['pending-sales', 'record'],
    mutationFn: async ({ productId, buyerEmail, buyerPhone }: RecordSaleParams) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/pending-ownership/admin',
        data: {
          product_id: productId,
          buyer_email: buyerEmail,
          buyer_phone: buyerPhone?.trim() || undefined,
        },
      })
      assertOk(response, 'Could not record the sale')
      return response.data as PendingSale
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingSalesKeys.all })
    },
  })
}

/**
 * Void, not delete. The row survives in a terminal `voided` state as the audit
 * of a sale that was retracted — which is the point of the requirement it
 * serves: the record has no expiry, so a refunded sale would otherwise assert a
 * sale that no longer happened, forever.
 *
 * A record the buyer has already CLAIMED is not voidable and the server answers
 * 409. Undoing a completed claim is a transfer reversal, not a void.
 */
export const useVoidPendingSale = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['pending-sales', 'void'],
    mutationFn: async ({ id }: { id: string }) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/pending-ownership/admin/${id}/void`,
      })
      assertOk(response, 'Could not void this record')
      return response.data as PendingSale
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pendingSalesKeys.all })
    },
  })
}
